<template>
  <b-overlay
    :show="show"
    variant="transparent"
    opacity="0.99"
    blur="5px"
    rounded="sm"
  >
    <template #overlay>
      <div class="text-center">
        <b-icon-controller
          font-scale="3"
          animation="cylon"
        />
        <p id="cancel-label">
          กรุณารอสักครู่...
        </p>
      </div>
    </template>
    <div>
      <div class="auth-wrapper auth-v1 px-2">
        <div class="auth-inner py-2">
          <b-card class="mb-0">
            <a href="/">
              <img
                src="/logo/logo.png"
                alt="logo"
                height="40"
              >
            </a>

            <validation-observer
              ref="loginForm"
              #default="{ invalid }"
            >
              <b-form
                class="auth-login-form"
                @submit.prevent="onPrimaryAction"
              >
                <!-- Step 0: credentials -->
                <template v-if="step === 'credentials'">
                  <b-form-group>
                    <label for="Username">ชื่อผู้ใช้งาน</label>
                    <validation-provider
                      #default="{ errors }"
                      name="Username"
                      vid="Username"
                      rules="required"
                    >
                      <b-form-input
                        id="Username"
                        v-model="userEmail"
                        :state="errors.length > 0 ? false : null"
                        name="Username"
                      />
                      <small class="text-danger">{{ errors[0] ? 'กรอกชื่อผู้ใช้งานให้ถูกต้อง' : '' }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group>
                    <label for="login-password">รหัสผ่าน</label>
                    <validation-provider
                      #default="{ errors }"
                      name="Password"
                      vid="password"
                      rules="required"
                    >
                      <b-input-group
                        class="input-group-merge"
                        :class="errors.length > 0 ? 'is-invalid' : null"
                      >
                        <b-form-input
                          id="login-password"
                          v-model="password"
                          :state="errors.length > 0 ? false : null"
                          class="form-control-merge"
                          :type="passwordFieldType"
                          name="login-password"
                          @keydown.enter.prevent="startLogin"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            class="cursor-pointer"
                            :icon="passwordToggleIcon"
                            @click="togglePasswordVisibility"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] ? 'กรอกรหัสผ่านให้ถูกต้อง' : '' }}</small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group>
                    <b-form-checkbox
                      id="remember-me"
                      v-model="status"
                      name="checkbox-1"
                    >
                      <span>จดจำฉันไว้ในระบบ</span>
                    </b-form-checkbox>
                  </b-form-group>

                  <b-button
                    variant="primary"
                    block
                    :disabled="invalid"
                    type="submit"
                  >
                    เข้าสู่ระบบ
                  </b-button>
                </template>

                <!-- Step: TOTP for enrolled users -->
                <template v-else-if="step === 'totp'">
                  <p class="mb-1">
                    กรอกรหัส 6 หลักจาก Google Authenticator
                  </p>
                  <b-form-group
                    label="Google Authenticator"
                    label-for="TOTP"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="TOTP"
                      vid="TOTP"
                      rules="required"
                    >
                      <b-form-input
                        id="TOTP"
                        v-model="totpCode"
                        :state="errors.length > 0 ? false : null"
                        name="TOTP"
                        maxlength="6"
                        autocomplete="one-time-code"
                        placeholder="000000"
                        @keydown.enter.prevent="verifyTotp"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <b-button
                    variant="primary"
                    block
                    :disabled="invalid"
                    type="submit"
                  >
                    ยืนยันและเข้าสู่ระบบ
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    block
                    class="mt-1"
                    @click="resetFlow"
                  >
                    กลับ
                  </b-button>
                </template>

                <!-- Step: legacy Telegram OTP (final time before GA enrollment) -->
                <template v-else-if="step === 'enroll_otp'">
                  <p class="mb-1">
                    ยืนยันตัวตนครั้งสุดท้ายด้วย OTP จาก Telegram ก่อนตั้งค่า Google Authenticator
                  </p>
                  <p
                    v-if="ref"
                    style="color: red"
                  >
                    ref : {{ ref }}
                  </p>
                  <b-form-group
                    label="OTP"
                    label-for="OTP"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="OTP"
                      vid="OTP"
                      rules="required"
                    >
                      <b-form-input
                        id="OTP"
                        v-model="legacyOtp"
                        :state="errors.length > 0 ? false : null"
                        name="OTP"
                        placeholder="กรุณากรอก OTP"
                        @keydown.enter.prevent="verifyEnrollmentOtp"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <b-button
                    variant="primary"
                    block
                    :disabled="invalid"
                    type="submit"
                  >
                    ยืนยัน OTP
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    block
                    class="mt-1"
                    @click="requestEnrollmentOtp"
                  >
                    ส่ง OTP อีกครั้ง
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    block
                    class="mt-1"
                    @click="resetFlow"
                  >
                    กลับ
                  </b-button>
                </template>

                <!-- Step: scan QR + confirm first TOTP -->
                <template v-else-if="step === 'enroll_setup'">
                  <p class="mb-1 font-weight-bold">
                    สแกน QR ด้วย Google Authenticator
                  </p>
                  <div
                    v-if="otpauthUrl"
                    class="text-center mb-2"
                  >
                    <qr-code
                      :text="otpauthUrl"
                      :size="200"
                    />
                  </div>
                  <p
                    v-if="manualKey"
                    class="small text-muted"
                  >
                    หรือใส่คีย์ด้วยตนเอง:
                    <strong>{{ manualKey }}</strong>
                  </p>
                  <b-form-group
                    label="รหัสยืนยันจากแอป"
                    label-for="ENROLL_TOTP"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="ENROLL_TOTP"
                      vid="ENROLL_TOTP"
                      rules="required"
                    >
                      <b-form-input
                        id="ENROLL_TOTP"
                        v-model="totpCode"
                        :state="errors.length > 0 ? false : null"
                        name="ENROLL_TOTP"
                        maxlength="6"
                        autocomplete="one-time-code"
                        placeholder="000000"
                        @keydown.enter.prevent="confirmEnrollment"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <b-button
                    variant="primary"
                    block
                    :disabled="invalid"
                    type="submit"
                  >
                    เปิดใช้และเข้าสู่ระบบ
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    block
                    class="mt-1"
                    @click="resetFlow"
                  >
                    ยกเลิก
                  </b-button>
                </template>
              </b-form>
            </validation-observer>
          </b-card>
        </div>
      </div>

      <img
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABaAAAACtBAMAAAC0KMWCAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAVUExURUdwTPHx9vDx9vDx9vHy9/Pz+e/w9XrTQnAAAAAGdFJOUwB7ptFPJqEDGaYAAAKASURBVHja7d3NacNAFIVRb1KAUoJUgkB7g0qIW1D/JcSEkEVwyJ/BulfnlKB8DO/NGHI6QYHLyzoM4zT7EuR6uqzr8HzNePvgoxB4GJ+HcZyX7Qafh5DD+H2muJmxoEmZKc7XmWKet5/yzciaKQRN6oK3CZq0w/jXM4WgSV3wBE3Vgido9rjgDX9d8ARN1YInaHaQ8fyAjAXNbu8pBE3VPYWg6R+NBU3qPYWgOeZhLGi+P4zffhUUl7Gg+TxTTCEzhaD5YqbY022boPnPTLFsrfyNLXiCJuEwLp0pBH2s07jpwRP04S8qtsPTQvxFxbTIWND5FxXaFXT+RYXDWNAFjx8yFrQFT9CYKQSNBU/QRz+MzRSCzj+MLXiCNlMg6P284MlY0AWHsZlC0OELnsNY0F7wEPROXvBkLOj8FzwzhaAteAjaCx6CvseCJ2NBmykQtAUPQd/jV0EyFnTBgmemELQFD0E//AXPgkdy0BY8CoJ220ZB0BY88oO24FEQtJmC/KAteBQE7QWP/KC94FEQtJmCgqC94JEftMOYgqAL/kc0grbgkR+0FzwKgvaCR37QFjwKgvaCR0HQFjw6gjZTUBW0T4CgQdAgaBA0ggZBg6BB0CBoBA2CBkGDoEHQCBoEDYIGQYOgETQIGgQNggZBI2gQNAgaBA2CRtAgaBA0CBoEjaBB0CBoEDSCBkGDoEHQIGgEDYIGQYOgQdAIGgQNggZBg6ARNAgaBA2CBkEjaBA0CBoEDYJG0CBoEDQIGgSNoEHQIGgQNAgaQYOgQdAgaAQNggZBg6BB0AgaBA2CBkGDoBE0CBoEDYIGQSNoEDQIGgQNgkbQIGgQNAgaBE2rV8fAx8SI153KAAAAAElFTkSuQmCC"
        alt=""
        class="img-login"
      >
    </div>
  </b-overlay>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BFormCheckbox,
  BCardText,
  BCard,
  BForm,
  BButton,
  BOverlay,
  VBTooltip,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import useJwt from '@/auth/jwt/useJwt'
import store from '@/store/index'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'
import BUID from 'uniquebrowserid'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    // eslint-disable-next-line vue/no-unused-components
    BCardText,
    BCard,
    BOverlay,
    BForm,
    BButton,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      show: false,
      step: 'credentials', // credentials | totp | enroll_otp | enroll_setup
      challengeToken: null,
      ref: null,
      legacyOtp: '',
      totpCode: '',
      otpauthUrl: '',
      manualKey: '',
      status: '',
      password: '',
      userEmail: '',
      sideImg: require('@/assets/images/pages/login-v2.svg'),
      required,
      email,
      ag: 0,
      urlori: window.location.origin,
      AgLog: {
        ip: null,
        latitude: '',
        longitude: '',
        buid: new BUID().completeID(),
      },
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/login-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
    apiBase() {
      if (this.ag && this.ag.endpoint) {
        return this.ag.endpoint.replace(/\/$/, '')
      }
      return null
    },
  },
  mounted() {
    this.getagid()
  },
  methods: {
    toastError(title, text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title,
          icon: 'CoffeeIcon',
          variant: 'danger',
          text: text || 'เกิดข้อผิดพลาด',
        },
      })
    },
    errorMessage(error) {
      return (error && error.response && error.response.data && error.response.data.message)
        || (error && error.message)
        || 'เกิดข้อผิดพลาด'
    },
    resetFlow() {
      this.step = 'credentials'
      this.challengeToken = null
      this.ref = null
      this.legacyOtp = ''
      this.totpCode = ''
      this.otpauthUrl = ''
      this.manualKey = ''
      this.show = false
    },
    clearEnrollmentSecrets() {
      this.otpauthUrl = ''
      this.manualKey = ''
      this.legacyOtp = ''
      this.totpCode = ''
    },
    getagid() {
      const agent_id = 1
      this.$http
        .get(`/agent/getEndpoint?agent_id=${agent_id}`)
        .then(response => {
          this.ag = response.data
          localStorage.setItem('ag', JSON.stringify(this.ag))
          this.show = false
        })
        .catch(error => console.log(error))
    },
    onPrimaryAction() {
      if (this.step === 'credentials') {
        this.startLogin()
      } else if (this.step === 'totp') {
        this.verifyTotp()
      } else if (this.step === 'enroll_otp') {
        this.verifyEnrollmentOtp()
      } else if (this.step === 'enroll_setup') {
        this.confirmEnrollment()
      }
    },
    startLogin() {
      this.$refs.loginForm.validate().then(success => {
        if (!success || !this.apiBase) return
        this.show = true
        axios.post(`${this.apiBase}/auth/login`, {
          email: this.userEmail,
          password: this.password,
          agent_id: this.ag.agent_id,
        })
          .then(response => {
            const data = response.data
            if (data && data.token) {
              this.finishLogin(data)
              return
            }
            if (data && data.status === 'MFA_REQUIRED') {
              this.challengeToken = data.challenge_token
              this.step = 'totp'
              this.totpCode = ''
              this.show = false
              return
            }
            if (data && data.status === 'MFA_ENROLLMENT_REQUIRED') {
              this.challengeToken = data.challenge_token
              this.step = 'enroll_otp'
              this.show = false
              this.requestEnrollmentOtp()
              return
            }
            this.show = false
            this.toastError('ไม่สามารถเข้าสู่ระบบได้', 'ตอบกลับจากเซิร์ฟเวอร์ไม่ถูกต้อง')
          })
          .catch(error => {
            this.show = false
            this.toastError('ไม่สามารถเข้าสู่ระบบได้', this.errorMessage(error))
          })
      })
    },
    verifyTotp() {
      if (!this.totpCode || !this.challengeToken || !this.apiBase) return
      this.show = true
      axios.post(`${this.apiBase}/mfa/verify`, {
        challenge_token: this.challengeToken,
        totp_code: this.totpCode,
      })
        .then(response => {
          this.finishLogin(response.data)
        })
        .catch(error => {
          this.show = false
          this.toastError('รหัสไม่ถูกต้อง', this.errorMessage(error))
        })
    },
    requestEnrollmentOtp() {
      if (!this.challengeToken || !this.apiBase) return
      this.show = true
      axios.post(`${this.apiBase}/mfa/enroll/request-otp`, {
        challenge_token: this.challengeToken,
      })
        .then(response => {
          this.ref = response.data.ref
          this.legacyOtp = ''
          this.show = false
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'ส่ง OTP แล้ว',
              icon: 'CoffeeIcon',
              variant: 'success',
              text: `ref: ${this.ref}`,
            },
          })
        })
        .catch(error => {
          this.show = false
          this.toastError('ส่ง OTP ไม่สำเร็จ', this.errorMessage(error))
        })
    },
    verifyEnrollmentOtp() {
      if (!this.legacyOtp || !this.ref || !this.challengeToken || !this.apiBase) return
      this.show = true
      axios.post(`${this.apiBase}/mfa/enroll/verify-otp`, {
        challenge_token: this.challengeToken,
        otps: this.legacyOtp,
        ref: this.ref,
      })
        .then(response => {
          const data = response.data
          this.challengeToken = data.challenge_token
          this.otpauthUrl = data.otpauth_url
          this.manualKey = data.manual_key
          this.totpCode = ''
          this.step = 'enroll_setup'
          this.show = false
        })
        .catch(error => {
          this.show = false
          this.toastError('OTP ไม่ถูกต้อง', this.errorMessage(error))
        })
    },
    confirmEnrollment() {
      if (!this.totpCode || !this.challengeToken || !this.apiBase) return
      this.show = true
      axios.post(`${this.apiBase}/mfa/enroll/confirm`, {
        challenge_token: this.challengeToken,
        totp_code: this.totpCode,
      })
        .then(response => {
          const data = response.data
          this.clearEnrollmentSecrets()
          if (data && data.token) {
            this.finishLogin(data)
            return
          }
          // Fallback: treat as enrolled and ask for TOTP again
          if (data && data.challenge_token) {
            this.challengeToken = data.challenge_token
            this.step = 'totp'
            this.totpCode = ''
            this.show = false
            return
          }
          this.show = false
          this.toastError('ลงทะเบียนไม่สำเร็จ', 'กรุณาลองใหม่')
        })
        .catch(error => {
          this.show = false
          this.toastError('ยืนยันไม่สำเร็จ', this.errorMessage(error))
        })
    },
    finishLogin(userData) {
      useJwt.setToken(userData.token)
      useJwt.setRefreshToken(userData.refreshToken)
      localStorage.setItem('userData', JSON.stringify(userData))
      this.$ability.update(userData.ability)
      if (userData.role === 'zean') {
        this.LogAdminZean()
      } else {
        this.LogAdmin()
      }
      this.clearEnrollmentSecrets()
      this.$router
        .push(getHomeRouteForLoggedInUser(userData.role))
        .then(() => {
          this.show = false
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Welcome to Image Dashboard',
              icon: 'CoffeeIcon',
              variant: 'success',
              text: 'You have successfully logged ',
            },
          })
        })
        .catch(() => {
          this.show = false
        })
    },
    LogAdmin() {
      const params = {
        on_page: 'login',
        ip: this.AgLog.ip,
        latitude: this.AgLog.latitude,
        longitude: this.AgLog.longitude,
        buid: this.AgLog.buid,
      }
      this.$http.get('/admin/log', { params })
    },
    LogAdminZean() {
      const params = {
        on_page: 'login',
        ip: this.AgLog.ip,
        latitude: this.AgLog.latitude,
        longitude: this.AgLog.longitude,
      }
      this.$http.get('/admin/log/zean', { params })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>

<style scoped>
.bg {
  background-image: url('/bg2.jpeg');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

.card {
  border-radius: 6px;
  box-shadow: 0 2px 10px 0 rgba(94, 86, 105, .1);
  background-color: rgb(255, 255, 255);
  padding: .9375rem .875rem;
}

label {
  font-size: 14px;
  font-weight: 400;
}
</style>
